import { whatsappColors, type MessageGroup } from './types';
import { ChatMessage } from './ChatMessage';

interface ChatAreaProps {
  messageGroups: MessageGroup[];
  contactName: string;
  contactInitial: string;
  isOnline: boolean;
  isLoading?: boolean;
  error?: string;
  onRetry?: () => void;
}

const WhatsAppBackground = () => (
  <div 
    className="absolute inset-0 pointer-events-none"
    style={{
      backgroundColor: whatsappColors.chatBg,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='360' height='360' viewBox='0 0 360 360' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='whatsapp-pattern' x='0' y='0' width='360' height='360' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 0h360v360H0z' fill='%23efeae2'/%3E%3Cpath d='M180 0c99.411 0 180 80.589 180 180s-80.589 180-180 180S0 279.411 0 180 80.589 0 180 0z' fill='none' stroke='%23d1d7db' stroke-width='0.5' opacity='0.04'/%3E%3Ccircle cx='90' cy='90' r='2' fill='%23000' opacity='0.02'/%3E%3Ccircle cx='270' cy='270' r='2' fill='%23000' opacity='0.02'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23whatsapp-pattern)'/%3E%3C/svg%3E")`,
    }}
  />
);

export function ChatArea({
  messageGroups,
  contactName,
  contactInitial,
  isOnline,
  isLoading,
  error,
  onRetry,
}: ChatAreaProps) {
  return (
    <div className="flex-1 h-full flex flex-col w-full" style={{ backgroundColor: whatsappColors.bg }}>
      <style>{`
        .whatsapp-chat-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .whatsapp-chat-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .whatsapp-chat-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(11, 20, 26, 0.1);
          border-radius: 3px;
        }
        .whatsapp-chat-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(11, 20, 26, 0.16);
        }
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .message-bubble {
          animation: slideIn 0.2s ease-out;
        }
      `}</style>

      {/* Header */}
      <div 
        className="px-4 py-2.5 flex items-center justify-between shrink-0"
        style={{ backgroundColor: whatsappColors.headerBg, borderBottom: `1px solid ${whatsappColors.borderDark}` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div 
            className="w-10 h-10 rounded-full bg-[#dfe5e7] flex items-center justify-center text-lg font-medium shrink-0"
            style={{ color: whatsappColors.textSecondary }}
          >
            {contactInitial}
          </div>
          <div className="min-w-0">
            <div 
              className="font-medium text-[16px] truncate"
              style={{ color: whatsappColors.textPrimary }}
            >
              {contactName}
            </div>
            <div 
              className="text-[13px] truncate"
              style={{ color: whatsappColors.textSecondary }}
            >
              {isOnline ? 'online' : 'clique para info do contato'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2" style={{ color: whatsappColors.iconGray }}>
          <button className="p-2 rounded-full transition-colors hover:bg-[#f5f6f6]" title="Buscar">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M15.009 13.805h-.636l-.22-.219a5.184 5.184 0 0 0 1.256-3.386 5.207 5.207 0 1 0-5.207 5.208 5.183 5.183 0 0 0 3.385-1.255l.221.22v.635l4.004 3.999 1.194-1.195-3.997-4.007zm-4.808 0a3.605 3.605 0 1 1 0-7.21 3.605 3.605 0 0 1 0 7.21z"/>
            </svg>
          </button>
          <button className="p-2 rounded-full transition-colors hover:bg-[#f5f6f6]" title="Menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 relative overflow-hidden w-full">
        <WhatsAppBackground />
        <div className="absolute inset-0 overflow-y-auto whatsapp-chat-scrollbar px-8 py-4 pb-6">
          {isLoading ? (
            <div className="text-center p-4" style={{ color: whatsappColors.textSecondary }}>
              Carregando mensagens...
            </div>
          ) : error ? (
            <div className="text-center p-4">
              <div style={{ color: whatsappColors.textSecondary }}>Falha ao carregar mensagens</div>
              {onRetry && (
                <button 
                  onClick={onRetry}
                  className="mt-2 px-4 py-2 rounded-lg"
                  style={{ backgroundColor: whatsappColors.green, color: 'white' }}
                >
                  Tentar novamente
                </button>
              )}
            </div>
          ) : messageGroups.length === 0 ? (
            <div className="text-center p-6" style={{ color: whatsappColors.textSecondary }}>
              Sem mensagens. Envie uma primeira mensagem para iniciar.
            </div>
          ) : (
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {messageGroups.map((group, groupIndex) => (
                <div key={groupIndex} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {/* Date badge */}
                  <div className="flex justify-center my-3">
                    <span 
                      className="text-[12.5px] font-medium px-3 py-1.5 rounded-lg shadow-sm"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', color: whatsappColors.textSecondary }}
                    >
                      {group.isToday ? 'HOJE' : group.date.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Messages */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                    {group.messages.map((message) => (
                      <ChatMessage key={message.id} message={message} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
